export const HEART_FRONT1_CLEARING_IDS = [
  "heart-front-1-clearing-1",
  "heart-front-1-clearing-2",
  "heart-front-1-clearing-3",
  "heart-front-1-clearing-4"
] as const;

export type HeartFront1ClearingId = (typeof HEART_FRONT1_CLEARING_IDS)[number];

export const HEART_FRONT1_ROUTE = {
  schema: "vaelor-heart-front1-route/v1",
  frontId: "heart-front-1",
  approachSectorId: "S1_0",
  requiredBestRing: 1,
  a1NodeId: "a1-margin",
  clearingNodeId: "camp-clearing",
  clearingIds: HEART_FRONT1_CLEARING_IDS,
  nodes: [
    {
      id: "a1-margin",
      role: "a1",
      lane: "shared",
      map: { x: 70, y: 250 },
      location: { sectorId: "S1_0", ring: 1, sector: 0, q: 0, r: 0 },
    },
    {
      id: "north-pass",
      role: "route",
      lane: "north",
      map: { x: 190, y: 135 },
      location: { sectorId: "S1_0", ring: 1, sector: 0, q: 1, r: -1 },
    },
    {
      id: "north-ruins",
      role: "route",
      lane: "north",
      map: { x: 335, y: 105 },
      location: { sectorId: "S1_0", ring: 1, sector: 0, q: 2, r: -1 },
    },
    {
      id: "miasma-lookout",
      role: "route",
      lane: "inner",
      map: { x: 485, y: 145 },
      location: { sectorId: "S1_0", ring: 1, sector: 0, q: 3, r: -1 },
    },
    {
      id: "south-ford",
      role: "route",
      lane: "south",
      map: { x: 190, y: 365 },
      location: { sectorId: "S1_0", ring: 1, sector: 0, q: 0, r: 1 },
    },
    {
      id: "south-woods",
      role: "route",
      lane: "south",
      map: { x: 350, y: 405 },
      location: { sectorId: "S1_0", ring: 1, sector: 0, q: 1, r: 1 },
    },
    {
      id: "crossroads",
      role: "route",
      lane: "shared",
      map: { x: 470, y: 285 },
      location: { sectorId: "S1_0", ring: 1, sector: 0, q: 1, r: 0 },
    },
    {
      id: "camp-clearing",
      role: "clearing",
      lane: "shared",
      map: { x: 610, y: 300 },
      location: { sectorId: "S1_0", ring: 1, sector: 0, q: 2, r: 0 },
    },
  ],
  edges: [
    { from: "a1-margin", to: "north-pass", lane: "north" },
    { from: "north-pass", to: "north-ruins", lane: "north" },
    { from: "north-ruins", to: "crossroads", lane: "north" },
    { from: "north-ruins", to: "miasma-lookout", lane: "inner" },
    { from: "miasma-lookout", to: "camp-clearing", lane: "inner" },
    { from: "a1-margin", to: "south-ford", lane: "south" },
    { from: "south-ford", to: "south-woods", lane: "south" },
    { from: "south-woods", to: "crossroads", lane: "south" },
    { from: "crossroads", to: "camp-clearing", lane: "inner" },
  ],
} as const;

function shortestRouteDistance(from: string, to: string): number {
  const neighbours = new Map<string, string[]>();
  for (const node of HEART_FRONT1_ROUTE.nodes) neighbours.set(node.id, []);
  for (const edge of HEART_FRONT1_ROUTE.edges) {
    neighbours.get(edge.from)?.push(edge.to);
    neighbours.get(edge.to)?.push(edge.from);
  }
  for (const adjacent of neighbours.values()) adjacent.sort();

  const queue: Array<{ nodeId: string; distance: number }> = [{ nodeId: from, distance: 0 }];
  const visited = new Set([from]);
  while (queue.length > 0) {
    const current = queue.shift();
    if (!current) break;
    if (current.nodeId === to) return current.distance;
    for (const nodeId of neighbours.get(current.nodeId) ?? []) {
      if (visited.has(nodeId)) continue;
      visited.add(nodeId);
      queue.push({ nodeId, distance: current.distance + 1 });
    }
  }
  throw new Error("heart_front1_route_unreachable");
}

export const HEART_FRONT1_REQUIRED_BEST_RING = HEART_FRONT1_ROUTE.requiredBestRing;
export const HEART_FRONT1_ROUTE_DISTANCE = shortestRouteDistance(
  HEART_FRONT1_ROUTE.a1NodeId,
  HEART_FRONT1_ROUTE.clearingNodeId
);

/** Assigns a Front 1 clearing from the server-owned player identity only. */
export function heartFront1ClearingForPlayer(
  playerId: string
): HeartFront1ClearingId {
  let hash = 0x811c9dc5;
  for (let index = 0; index < playerId.length; index += 1) {
    hash ^= playerId.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return HEART_FRONT1_CLEARING_IDS[hash % HEART_FRONT1_CLEARING_IDS.length];
}

export type HeartFront1Id = typeof HEART_FRONT1_ROUTE.frontId;
export type HeartFront1RouteNode = (typeof HEART_FRONT1_ROUTE.nodes)[number];
export type HeartFront1RouteNodeId = HeartFront1RouteNode["id"];
export type HeartFront1ServerLocation = HeartFront1RouteNode["location"];
